/**
 * Dropdown
 * Styled dropdown wrapper
 */
import React, { useRef } from 'react';

import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel';

import { DropdownArrow, ErrorMessage, NewDropdown } from './Dropdown.style';

const Dropdown: React.FC<
  Omit<
    Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value'>,
    'onChange'
  > & {
    placeholder?: string;
    onChange?: (value: string) => void;
    width?: string;
    height?: string;
    error?: string;
    required?: boolean;
    label?: string;
    /**
     * Accessible name for the select. Needed where the visible label is hidden —
     * the label element here has no `htmlFor`, so hiding it leaves the select
     * unnamed.
     */
    ariaLabel?: string;
    value?: number | string | null;
    children?: React.ReactNode;
    color?: string;
  }
> = ({
  placeholder = 'Select',
  onChange,
  width = '100%',
  height = '45px',
  error = '',
  required = false,
  label = '',
  ariaLabel,
  value = '',
  children,
  color,
  ...props
}) => {
  function handleChange(event: React.ChangeEvent<HTMLSelectElement>) {
    onChange?.(event.target.value);
  }
  const selectRef = useRef<HTMLSelectElement>(null);

  return (
    <div style={{ width, fontSize: 16 }}>
      <InputLabel label={label} required={required} style={{ color: color }} />
      <NewDropdown error={Boolean(error)} height={height} width={width}>
        <select
          aria-label={ariaLabel}
          value={value ?? ''}
          {...props}
          onChange={handleChange}
          ref={selectRef}
        >
          <option disabled value="">
            {placeholder}
          </option>
          {children}
        </select>
        <DropdownArrow onClick={() => selectRef.current?.focus()}>
          <Icon color="text.primary" name="chevron-down" />
        </DropdownArrow>
      </NewDropdown>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
    </div>
  );
};

export default Dropdown;
